import { useLocation, useNavigate } from 'react-router-dom'
import { useCallback, useEffect, useState } from 'react'
import { AIReportData, getLatestAIReportForSupplier, getLatestReportForSource, getTimeseriesForSupplier, ReportData, ReportTimeseriesData, requestAIReportForHost, requestAIReportForSupplier } from '../remote/reports'
import { getOrganizationSuppliers, SupplierData } from '../remote/suppliers'
import { Pie, ResponsivePie } from '@nivo/pie'
import { LineSeries, ResponsiveLine } from '@nivo/line'
import { getFinishedScanCountForSource, getScanCountForSource } from '../remote/scan'
import { FQDNSimple } from './FQDN'
import { IPSimple } from './IP'
import { WeaknessInstanceSimple } from './Weakness'
import { VulnerabilityInstanceSimple } from './Vulnerability'
import { Box, Button, Card, CardContent, CardHeader, CircularProgress, Divider, FormControl, Grid, IconButton, InputLabel, List, ListItem, MenuItem, Paper, Select, SelectChangeEvent, Stack, Tab, Tabs, Typography } from '@mui/material'
import { More, MoreVert } from '@mui/icons-material'
import { EntityColors, SeverityColors } from '../colors'

export class ManageDashboardProps {
	org: string;

	constructor(org: string) {
		this.org = org;
	}
}

export const ManageDashboard = (props: ManageDashboardProps) => {
	const navigate = useNavigate()
	const location = useLocation()

	const [suppliers, setSuppliers] = useState<SupplierData[] | null>(null)
	const [isLoaded, setIsLoaded] = useState(false)

	useEffect(() => {
		getOrganizationSuppliers(props.org, (data, resp) => {
			if (data) {
				setSuppliers(data);
				setIsLoaded(true);
			} else {
				setSuppliers(null);
				setIsLoaded(false);
			}
		});
	}, [setSuppliers, setIsLoaded]);

	const [selectedSupplier, setSelectedSupplier] = useState<string>(location?.state?.supplier ?? "")

	const setSupplier = useCallback((ev: SelectChangeEvent) => {
		setSelectedSupplier(ev.target.value as string)
		navigate("#", {state: {supplier: ev.target.value as string}, relative: 'route', preventScrollReset: true})
	}, [setSelectedSupplier])

	return (<>{!isLoaded ? <CircularProgress /> : <> {((suppliers?.length ?? 0) == 0) ? <><Typography variant="h5">You have no suppliers registered, please add some in the Suppliers tab.</Typography></> : <>
		<FormControl fullWidth>
			<InputLabel id="supplier-select-label">Supplier</InputLabel>
			<Select label="supplier-select-label" defaultValue={suppliers?.at(0)?.id ?? ""} value={selectedSupplier} onChange={setSupplier}>
				{suppliers?.map((item, i) => {
					return (<MenuItem value={item.id}>
						{item.name}
					</MenuItem>)
				})}
			</Select>
		</FormControl>
		<Divider orientation='horizontal' variant='fullWidth' sx={{mt:"1em", mb: "1em", alignSelf:"stretch"}} />
		{(selectedSupplier != "") ? (<>
			<SupplierReportDashboard key={selectedSupplier} supplier={selectedSupplier}></SupplierReportDashboard>
		</>) : (<><Typography variant="h5">Select a supplier!</Typography></>)}
	</>}</>}</>)
}

class SupplierReportDashboardProps {
	supplier: string;

	constructor(supplier: string) {
		this.supplier = supplier;
	}
}

const SupplierReportDashboard = (props: SupplierReportDashboardProps) => {
	const navigate = useNavigate()

	const [report, setReport] = useState<ReportData | null>(null);
	const [isLoaded, setIsLoaded] = useState(false)
	useEffect(() => {
		getLatestReportForSource(props.supplier, (data, resp) => {
			if (data) {
				setReport(data);
				setIsLoaded(true);
			} else {
				setReport(null);
				setIsLoaded(false);
			}
		});
	}, [setReport, setIsLoaded]);

	const [finishedScanCount, setFinishedScanCount] = useState<number | null>(null);
	const [isFinishedScanCountLoaded, setIsFinishedScanCountLoaded] = useState(false)
	useEffect(() => {
		getFinishedScanCountForSource(props.supplier, (data, resp) => {
			if (data != null) {
				setFinishedScanCount(data);
				setIsFinishedScanCountLoaded(true);
			} else {
				setFinishedScanCount(null);
				setIsFinishedScanCountLoaded(false);
			}
		});
	}, [setFinishedScanCount, setIsFinishedScanCountLoaded]);

	const [totalScanCount, setTotalScanCount] = useState<number | null>(null);
	const [isTotalScanCountLoaded, setIsTotalScanCountLoaded] = useState(false)
	useEffect(() => {
		getScanCountForSource(props.supplier, (data, resp) => {
			if (data != null) {
				setTotalScanCount(data);
				setIsTotalScanCountLoaded(true);
			} else {
				setTotalScanCount(null);
				setIsTotalScanCountLoaded(false);
			}
		});
	}, [setTotalScanCount, setIsTotalScanCountLoaded]);

	const [topIps, setTopIps] = useState<string[]>([])
	useEffect(() => {
		if (report) {
			const reportVals: string = report.values["attack_surface.top_10_ips"] ?? report.values["attack_surface.top_5_ips"] ?? "";
			if (reportVals) {
				setTopIps(reportVals
					.split(',').map((v) => v.split(":"))
					.sort((a, b) => parseFloat(b[1]) - parseFloat(a[1]))
					.map((v) => v[0]))
			}
		}
	}, [report, setTopIps])

	const [topDomains, setTopDomains] = useState<string[]>([])
	useEffect(() => {
		if (report) {
			const reportVals: string = report.values["attack_surface.top_10_domains"] ?? report.values["attack_surface.top_5_domains"] ?? "";
			if (reportVals) {
				setTopDomains(reportVals
					.split(',').map((v) => v.split(":"))
					.sort((a, b) => parseFloat(b[1]) - parseFloat(a[1]))
					.map((v) => v[0]))
			}
		}
	}, [report, setTopDomains])

	const [topWeaknesses, setTopWeaknesses] = useState<string[]>([])
	useEffect(() => {
		if (report) {
			const reportVals: string = report.values["attack_surface.top_10_weaknesses"] ?? report.values["attack_surface.top_5_weaknesses"] ?? "";
			if (reportVals) {
				setTopWeaknesses(reportVals
					.split(',').map((v) => v.split(":"))
					.sort((a, b) => parseFloat(b[1]) - parseFloat(a[1]))
					.map((v) => v[0]))
			}
		}
	}, [report, setTopWeaknesses])

	const [topVulnerabilities, setTopVulnerabilities] = useState<string[]>([])
	useEffect(() => {
		if (report) {
			const reportVals: string = report.values["attack_surface.top_10_vulnerabilities"] ?? report.values["attack_surface.top_5_vulnerabilities"] ?? "";
			if (reportVals) {
				setTopVulnerabilities(reportVals
					.split(',').map((v) => v.split(":"))
					.sort((a, b) => parseFloat(b[1]) - parseFloat(a[1]))
					.map((v) => v[0]))
			}
		}
	}, [report, setTopVulnerabilities])

	const [reportState, setReportState] = useState<'READY' | 'GENERATING'>('READY');
	const [reportData, setReportData] = useState<AIReportData | null | undefined>(undefined);

	const checkReportStatusPing = useCallback(() => {
		getLatestAIReportForSupplier(props.supplier, (data, resp) => {
			if (data) {
				if (data.finished_at) {
					setReportState('READY');
				} else {
					setTimeout(checkReportStatusPing, 2000 + 1000 * Math.random())
				}

				if (data.id != reportData?.id && data.finished_at != reportData?.finished_at) {
					setReportData(data);
				}
			} else {
				setReportData(null);
			}
		})
	}, [reportData, setReportData])

	useEffect(() => {
		getLatestAIReportForSupplier(props.supplier, (data, resp) => {
			if (data) {
				if (data.finished_at) {
					setReportState('READY');
				} else {
					setReportState('GENERATING');
					setTimeout(checkReportStatusPing, 2000 + 1000 * Math.random());
				}
				setReportData(data);
			} else {
				setReportData(null);
			}
		})
	}, [setReportData])

	return (<>{(!isLoaded || report == null) ? <CircularProgress/> : <>
		<Stack direction='column' width='100%' gap='1em'>
			<Stack direction={'row'}>
				<Typography variant="h3">Attack surface report</Typography>
				<Box sx={{ml:'auto'}}>
					{
						reportData === undefined
							? <CircularProgress/>
							: ((reportData === null && reportState != 'GENERATING')
								? <Button onClick={() => {
									setReportState('GENERATING');
									requestAIReportForSupplier(props.supplier, (data, resp) => {
										if (data) {
											if (data.finished_at) {
												setReportState('READY');
											} else {
												setTimeout(checkReportStatusPing, 2000 + 1000 * Math.random())
											}
											setReportData(data);
										} else {
											setReportData(null);
										}
									})
								}}>
									<Typography variant="body1">Generate summary</Typography>
								</Button>
								: ((reportState == 'GENERATING')
									? <Button disabled>Generating.. <CircularProgress size='24px' /></Button>
									: (reportData != null && reportState == 'READY') ?(<Button onClick={() => navigate(`/reports/ai/supplier/${reportData.id}`)}>
										<Typography variant="body1">View summary</Typography>
									</Button>) : <Typography>Error</Typography>))
					}
				</Box>
			</Stack>
			{isTotalScanCountLoaded && isFinishedScanCountLoaded
				? <Typography variant="h5">Scans complete: {finishedScanCount} of {totalScanCount}</Typography>
				: <><Typography variant="h5">Scans complete:</Typography> <CircularProgress /></>
			}
			<Stack direction={'row'} width='100%'>
				<Stack direction={'column'} width='100%'>
					<FindingsPerCategory supplierId={props.supplier} report={report}/>
				</Stack>
			</Stack>
			<Grid container spacing={2}>
				<Grid size={{sm: 12, md: 6, lg: 6, xl: 4}}>
					<TopDomainFindings ids={topDomains}/>
				</Grid>
				<Grid size={{sm: 12, md: 6, lg: 6, xl: 4}}>
					<TopIpFindings ids={topIps}/>
				</Grid>
				<Grid size={{sm: 12, md: 6, lg: 6, xl: 4}}>
					<VulnerabilitiesBySeverity vulnerabilitiesBySeverityStr={report.values["attack_surface.vulnerabilities_by_severity"] ?? ""}/>
				</Grid>
				<Grid size={{sm: 12, md: 6, lg: 6, xl: 4}}>
					<WeaknessesBySeverity weaknessesBySeverityStr={report.values["attack_surface.weaknesses_by_severity"] ?? ""}/>
				</Grid>
				<Grid size={{sm: 12, md: 6, lg: 6, xl: 4}}>
					<TopVulnerabilityFindings ids={topVulnerabilities}/>
				</Grid>
				<Grid size={{sm: 12, md: 6, lg: 6, xl: 4}}>
					<TopWeaknessFindings ids={topWeaknesses}/>
				</Grid>
			</Grid>
		</Stack>
	</>}</>)
}

class CategoryFindingProps {
	text: string
	value: string

	constructor(text: string, value: string) {
		this.text = text;
		this.value = value;
	}
}

const CategoryFindingEntry = (props: CategoryFindingProps) => {
	return (<>
		<ListItem sx={{width:'100%'}}>
			<Typography variant="body1" sx={{mr:'4em'}}>{props.text}</Typography>
			<Divider orientation='vertical' sx={{alignSelf: "stretch", ml: "auto", mr:"1em"}} />
			<Stack direction='row' width={40}>
				<Typography variant="body1" sx={{ml:'auto'}} justifySelf={'flex-end'}>{props.value}</Typography>
			</Stack>
		</ListItem>
		<Divider orientation='horizontal' sx={{alignSelf:"stretch"}} />
	</>)
}

interface FindingsPerCategoryGraphTabProps {
	children?: React.ReactNode
	index: number
	value: number
}

const FindingsPerCategoryGraphTab = (props: FindingsPerCategoryGraphTabProps) => {
	const { children, value, index, ...other } = props;
	return <div
		role="tabpanel"
		hidden={value !== index}
		id={`simple-tabpanel-${index}`}
		aria-labelledby={`simple-tab-${index}`}
		style={{width:'100%', height: '100%'}}
		{...other}
	>
		{value === index && <Box sx={{ width: '100%', height: '100%' }}>{children}</Box>}
	</div>
}

interface FindingsLineSeries extends LineSeries {
	color: string
}

class FindingsPerCategoryResponsiveLineProps {
	data: FindingsLineSeries[]

	constructor(data: FindingsLineSeries[]) {
		this.data = data;
	}
}

const FindingsPerCategoryResponsiveLine = (props: FindingsPerCategoryResponsiveLineProps) => {
	return <ResponsiveLine
			data={props.data}
			margin={{ top: 50, right: 110, bottom: 70, left: 60 }}
			// yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: true, reverse: false }}
			axisBottom={{tickValues: 10, format: '%Y-%m-%d', tickRotation: 45}}
			pointSize={5}
			pointColor={{ theme: 'background' }}
			pointBorderWidth={2}
			colors={(data) => data.color}
			pointBorderColor={{ from: 'seriesColor' }}
			pointLabelYOffset={-12}
			enableTouchCrosshair={true}
			useMesh={true}
			xScale={{
				type: 'time',
				format: '%Y-%m-%d',
				useUTC: false,
			}}
			animate
			enableSlices={'x'}
			legends={[
				{
					anchor: 'bottom-right',
					direction: 'column',
					translateX: 100,
					itemWidth: 90,
					itemHeight: 22,
					symbolShape: 'circle'
				}
			]}
		/>
}

class FindingsPerCategoryGraphProps {
	supplierId: string

	constructor(supplierId: string) {
		this.supplierId = supplierId;
	}
}

function a11yProps(index: number) {
	return {
		id: `simple-tab-${index}`,
		'aria-controls': `simple-tabpanel-${index}`,
	};
}

const FindingsPerCategoryGraph = (props: FindingsPerCategoryGraphProps) => {
	const [portTimeseries, setPortTimeseries] = useState<ReportTimeseriesData[] | null>(null);
	const [isPortTimeseriesLoaded, setIsPortTimeseriesLoaded] = useState(false)
	useEffect(() => {
		getTimeseriesForSupplier(props.supplierId, "attack_surface.num_ports", (data, resp) => {
			if (data) {
				setPortTimeseries(data);
				setIsPortTimeseriesLoaded(true);
			} else {
				setPortTimeseries(null);
				setIsPortTimeseriesLoaded(false);
			}
		});
	}, [setPortTimeseries, setIsPortTimeseriesLoaded]);

	const [domainTimeseries, setDomainTimeseries] = useState<ReportTimeseriesData[] | null>(null);
	const [isDomainTimeseriesLoaded, setIsDomainTimeseriesLoaded] = useState(false)
	useEffect(() => {
		getTimeseriesForSupplier(props.supplierId, "attack_surface.num_domains", (data, resp) => {
			if (data) {
				setDomainTimeseries(data);
				setIsDomainTimeseriesLoaded(true);
			} else {
				setDomainTimeseries(null);
				setIsDomainTimeseriesLoaded(false);
			}
		});
	}, [setDomainTimeseries, setIsDomainTimeseriesLoaded]);

	const [IPTimeseries, setIPTimeseries] = useState<ReportTimeseriesData[] | null>(null);
	const [isIPTimeseriesLoaded, setIsIPTimeseriesLoaded] = useState(false)
	useEffect(() => {
		getTimeseriesForSupplier(props.supplierId, "attack_surface.num_ips", (data, resp) => {
			if (data) {
				setIPTimeseries(data);
				setIsIPTimeseriesLoaded(true);
			} else {
				setIPTimeseries(null);
				setIsIPTimeseriesLoaded(false);
			}
		});
	}, [setIPTimeseries, setIsIPTimeseriesLoaded]);

	const [certificateTimeseries, setCertificateTimeseries] = useState<ReportTimeseriesData[] | null>(null);
	const [isCertificateTimeseriesLoaded, setIsCertificateTimeseriesLoaded] = useState(false)
	useEffect(() => {
		getTimeseriesForSupplier(props.supplierId, "attack_surface.num_certificates", (data, resp) => {
			if (data) {
				setCertificateTimeseries(data);
				setIsCertificateTimeseriesLoaded(true);
			} else {
				setCertificateTimeseries(null);
				setIsCertificateTimeseriesLoaded(false);
			}
		});
	}, [setCertificateTimeseries, setIsCertificateTimeseriesLoaded]);

	const [technologyTimeseries, setTechnologyTimeseries] = useState<ReportTimeseriesData[] | null>(null);
	const [isTechnologyTimeseriesLoaded, setIsTechnologyTimeseriesLoaded] = useState(false)
	useEffect(() => {
		getTimeseriesForSupplier(props.supplierId, "attack_surface.num_technologies", (data, resp) => {
			if (data) {
				setTechnologyTimeseries(data);
				setIsTechnologyTimeseriesLoaded(true);
			} else {
				setTechnologyTimeseries(null);
				setIsTechnologyTimeseriesLoaded(false);
			}
		});
	}, [setTechnologyTimeseries, setIsTechnologyTimeseriesLoaded]);

	const [weaknessTimeseries, setWeaknessTimeseries] = useState<ReportTimeseriesData[] | null>(null);
	const [isWeaknessTimeseriesLoaded, setIsWeaknessTimeseriesLoaded] = useState(false)
	useEffect(() => {
		getTimeseriesForSupplier(props.supplierId, "attack_surface.num_weaknesses", (data, resp) => {
			if (data) {
				setWeaknessTimeseries(data);
				setIsWeaknessTimeseriesLoaded(true);
			} else {
				setWeaknessTimeseries(null);
				setIsWeaknessTimeseriesLoaded(false);
			}
		});
	}, [setWeaknessTimeseries, setIsWeaknessTimeseriesLoaded]);

	const [vulnerabilityTimeseries, setVulnerabilityTimeseries] = useState<ReportTimeseriesData[] | null>(null);
	const [isVulnerabilityTimeseriesLoaded, setIsVulnerabilityTimeseriesLoaded] = useState(false)
	useEffect(() => {
		getTimeseriesForSupplier(props.supplierId, "attack_surface.num_vulnerabilities", (data, resp) => {
			if (data) {
				setVulnerabilityTimeseries(data);
				setIsVulnerabilityTimeseriesLoaded(true);
			} else {
				setVulnerabilityTimeseries(null);
				setIsVulnerabilityTimeseriesLoaded(false);
			}
		});
	}, [setVulnerabilityTimeseries, setIsVulnerabilityTimeseriesLoaded]);

	const [currentTab, setCurrentTab] = useState(0);
	 const handleChange = (event: React.SyntheticEvent, newValue: number) => {
		setCurrentTab(newValue);
	};

	const data = [{
		id: "ports",
		data: portTimeseries?.map((v) => {return {
			x: v.created_at,
			y: v.value
		}}) ?? [],
		color: EntityColors.ports
	}, {
		id: "domains",
		data: domainTimeseries?.map((v) => {return {
			x: v.created_at,
			y: v.value
		}}) ?? [],
		color: EntityColors.domains
	}, {
		id: "IPs",
		data: IPTimeseries?.map((v) => {return {
			x: v.created_at,
			y: v.value
		}}) ?? [],
		color: EntityColors.ips
	}, {
		id: "certificates",
		data: certificateTimeseries?.map((v) => {return {
			x: v.created_at,
			y: v.value
		}}) ?? [],
		color: EntityColors.certificates
	}, {
		id: "technologies",
		data: technologyTimeseries?.map((v) => {return {
			x: v.created_at,
			y: v.value
		}}) ?? [],
		color: EntityColors.technologies
	}, {
		id: "weaknesses",
		data: weaknessTimeseries?.map((v) => {return {
			x: v.created_at,
			y: v.value
		}}) ?? [],
		color: EntityColors.weaknesses
	}, {
		id: "vulnerabilities",
		data: vulnerabilityTimeseries?.map((v) => {return {
			x: v.created_at,
			y: v.value
		}}) ?? [],
		color: EntityColors.vulnerabilities
	}];

	return (<>{[isDomainTimeseriesLoaded, isIPTimeseriesLoaded, isPortTimeseriesLoaded, isCertificateTimeseriesLoaded, isTechnologyTimeseriesLoaded, isWeaknessTimeseriesLoaded, isVulnerabilityTimeseriesLoaded].every((x) => x) ?
			<><Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
	<Tabs value={currentTab} onChange={handleChange} aria-label="basic tabs example">
		<Tab label="All" {...a11yProps(0)} />
		<Tab label="FQDNs" {...a11yProps(1)} />
		<Tab label="IPs" {...a11yProps(2)} />
		<Tab label="Ports" {...a11yProps(3)} />
		<Tab label="Certificates" {...a11yProps(4)} />
		<Tab label="Technologies" {...a11yProps(5)} />
		<Tab label="Weaknesses" {...a11yProps(6)} />
		<Tab label="Vulnerabilities" {...a11yProps(7)} />
	</Tabs>
	</Box>
	<FindingsPerCategoryGraphTab value={currentTab} index={0}>
		<FindingsPerCategoryResponsiveLine data={data} />
	</FindingsPerCategoryGraphTab>
	<FindingsPerCategoryGraphTab value={currentTab} index={1}>
		<FindingsPerCategoryResponsiveLine data={data.filter((x) => x.id == "domains")} />
	</FindingsPerCategoryGraphTab>
	<FindingsPerCategoryGraphTab value={currentTab} index={2}>
		<FindingsPerCategoryResponsiveLine data={data.filter((x) => x.id == "IPs")} />
	</FindingsPerCategoryGraphTab>
	<FindingsPerCategoryGraphTab value={currentTab} index={3}>
		<FindingsPerCategoryResponsiveLine data={data.filter((x) => x.id == "ports")} />
	</FindingsPerCategoryGraphTab>
	<FindingsPerCategoryGraphTab value={currentTab} index={4}>
		<FindingsPerCategoryResponsiveLine data={data.filter((x) => x.id == "certificates")} />
	</FindingsPerCategoryGraphTab>
	<FindingsPerCategoryGraphTab value={currentTab} index={5}>
		<FindingsPerCategoryResponsiveLine data={data.filter((x) => x.id == "technologies")} />
	</FindingsPerCategoryGraphTab>
	<FindingsPerCategoryGraphTab value={currentTab} index={6}>
		<FindingsPerCategoryResponsiveLine data={data.filter((x) => x.id == "weaknesses")} />
	</FindingsPerCategoryGraphTab>
	<FindingsPerCategoryGraphTab value={currentTab} index={7} >
		<FindingsPerCategoryResponsiveLine data={data.filter((x) => x.id == "vulnerabilities")} />
	</FindingsPerCategoryGraphTab>
	</> : <CircularProgress />}
	</>)
}

class FindingsPerCategoryProps {
	supplierId: string
	report: ReportData

	constructor(supplierId: string, report: ReportData) {
		this.supplierId = supplierId;
		this.report = report;
	}
}

const FindingsPerCategory = (props: FindingsPerCategoryProps) => {
	const data = [
		{
			id: "domains",
			label: "Domains",
			value: props.report.values["attack_surface.num_domains"] ?? 0,
			color: EntityColors.domains
		},
		{
			id: "ips",
			label: "IPs",
			value: props.report.values["attack_surface.num_ips"] ?? 0,
			color: EntityColors.ips
		},
		{
			id: "ports",
			label: "Ports",
			value: props.report.values["attack_surface.num_ports"] ?? 0,
			color: EntityColors.ports
		},
		{
			id: "technologies",
			label: "Technologies",
			value: props.report.values["attack_surface.num_technologies"] ?? 0,
			color: EntityColors.technologies
		},
		{
			id: "certificates",
			label: "Certificates",
			value: props.report.values["attack_surface.num_certificates"] ?? 0,
			color: EntityColors.certificates
		},
		{
			id: "weaknesses",
			label: "Suspected Weaknesses",
			value: props.report.values["attack_surface.num_weaknesses"] ?? 0,
			color: EntityColors.weaknesses
		},
		{
			id: "vulnerabilities",
			label: "Suspected Vulnerabilities",
			value: props.report.values["attack_surface.num_vulnerabilities"] ?? 0,
			color: EntityColors.vulnerabilities
		},
	]

	return (<>
		<Card sx={{width: '100%'}}>
			<CardHeader title={<Typography variant="h5">Findings per category</Typography>} action={
					<IconButton aria-label="settings">
						<MoreVert />
					</IconButton>
				}/>
			<Divider />
			<CardContent>
				<Grid container spacing={2} width={'100%'}>
					<Grid size={{md: 12, lg: 6}} width={'100%'}>
						<List sx={{width: '100%'}}>
							{
								data.map((val) => (
									<CategoryFindingEntry text={val.label} value={val.value} />
								))
							}
						</List>
					</Grid>
					<Grid size={{md: 12, lg: 6}} width={'100%'}>
						<Box minHeight={250} maxHeight={300} width={'100%'} sx={{aspectRatio: 1/1}}>
							<ResponsivePie
								data={data}
								margin={{ top: 40, right: 40, bottom: 40, left: 40 }}
								innerRadius={0.5}
								padAngle={0.6}
								cornerRadius={2}
								activeOuterRadiusOffset={8}
								arcLinkLabelsSkipAngle={10}
								arcLinkLabelsTextColor="#333333"
								arcLinkLabelsThickness={2}
								arcLabelsSkipAngle={10}
								colors={(data) => data.data.color}
								legends={[
									{
										anchor: 'bottom',
										direction: 'column',
										translateX: 170,
										translateY: 20,
										itemWidth: 50,
										itemHeight: 18,
										symbolShape: 'circle'
									}
								]}
							/>
						</Box>
					</Grid>
				</Grid>
				<Divider orientation='horizontal' sx={{alignSelf:"stretch"}} />
				<Stack height={400}><FindingsPerCategoryGraph supplierId={props.supplierId} /></Stack>
			</CardContent>
		</Card>
	</>)
}

class TopIpFindingEntryProps {
	position: number;
	ip_id: string;

	constructor(position: number, ip_id: string) {
		this.position = position;
		this.ip_id = ip_id;
	}
}

const TopIpFindingEntry = (props: TopIpFindingEntryProps) => {
	return (<>
		<ListItem sx={{width: 'auto'}}>
			<Typography variant="body1" width={25} justifySelf={'flex-start'}>{props.position+1}</Typography>
			<Divider orientation='vertical' sx={{height: 'auto', alignSelf: "stretch", ml: "auto", mr:"1em"}} />
			<Box width='100%'>
				<IPSimple id={props.ip_id} />
			</Box>
		</ListItem>
		<Divider sx={{alignSelf:"stretch"}} />
	</>)
}

class TopIpFindingsProps {
	ids: string[]

	constructor(ids: string[]) {
		this.ids = ids;
	}
}

const TopIpFindings = (props: TopIpFindingsProps) => {
	return (<>
		<Card>
			<CardHeader title={<Typography variant="h5">Top IPs by attack surface size</Typography>} action={
					<IconButton aria-label="settings">
						<MoreVert />
					</IconButton>
				}/>
			<Divider />
			<CardContent>
				<List>
				{
					props.ids.map((value, index) => {
						return (<>
							<TopIpFindingEntry position={index} ip_id={value} />
						</>)
					})
				}
				</List>
			</CardContent>
		</Card>
	</>)
}

class TopDomainFindingEntryProps {
	position: number;
	fqdn_id: string;

	constructor(position: number, fqdn_id: string) {
		this.position = position;
		this.fqdn_id = fqdn_id;
	}
}

const TopDomainFindingEntry = (props: TopDomainFindingEntryProps) => {
	return (<>
		<ListItem divider>
			<Typography variant="body1" width={25} justifySelf={'flex-end'}>{props.position+1}</Typography>
			<Divider orientation='vertical' sx={{height: 'auto', alignSelf: "stretch", ml: "auto", mr:"1em"}} />
			<Box width='100%'>
				<FQDNSimple id={props.fqdn_id} data={undefined} />
			</Box>
		</ListItem>
	</>)
}

class TopDomainFindingsProps {
	ids: string[]

	constructor(ids: string[]) {
		this.ids = ids;
	}
}

const TopDomainFindings = (props: TopDomainFindingsProps) => {
	return (<>
		<Card>
			<CardHeader title={<Typography variant="h5">Top domains by attack surface size</Typography>} action={
					<IconButton aria-label="settings">
						<MoreVert />
					</IconButton>
				}/>
			<Divider />
			<CardContent>
				<List>
					{
						props.ids.map((value, index) => {
							return (<>
								<TopDomainFindingEntry position={index} fqdn_id={value} />
							</>)
						})
					}
				</List>
			</CardContent>
		</Card>
	</>)
}

class TopWeaknessFindingEntryProps {
	position: number;
	weakness_id: string;

	constructor(position: number, weakness_id: string) {
		this.position = position;
		this.weakness_id = weakness_id;
	}
}

const TopWeaknessFindingEntry = (props: TopWeaknessFindingEntryProps) => {
	return (<>
		<ListItem>
			<Typography variant="body1" width={25} justifySelf={'flex-start'}>{props.position+1}</Typography>
			<Divider orientation='vertical' sx={{height: 'auto', alignSelf: "stretch", ml: "auto", mr:"1em"}} />
			<Box width='100%'>
				<WeaknessInstanceSimple id={props.weakness_id} data={undefined} />
			</Box>
		</ListItem>
		<Divider sx={{alignSelf:"stretch"}} />
	</>)
}

class TopWeaknessFindingsProps {
	ids: string[]

	constructor(ids: string[]) {
		this.ids = ids;
	}
}

const TopWeaknessFindings = (props: TopWeaknessFindingsProps) => {
	return (<>
		<Card>
			<CardHeader title={<Typography variant="h5">Top suspected weaknesses</Typography>} action={
					<IconButton aria-label="settings">
						<MoreVert />
					</IconButton>
				}/>
			<Divider />
			<CardContent>
				<List>
				{
					props.ids.map((value, index) => {
						return (<>
							<TopWeaknessFindingEntry position={index} weakness_id={value} />
						</>)
					})
				}
				</List>
			</CardContent>
		</Card>
	</>)
}

class WeaknessesBySeverityProps {
	weaknessesBySeverityStr: string

	constructor(weaknessesBySeverityStr: string) {
		this.weaknessesBySeverityStr = weaknessesBySeverityStr;
	}
}

const WeaknessesBySeverity = (props: WeaknessesBySeverityProps) => {
	const weaknessesBySeverity = new Map(props.weaknessesBySeverityStr.split(",").map((x) => x.split(":")).map((x) => [x[0], x[1]]))

	const data = [
		{id: "Critical", label: "Critical", value: weaknessesBySeverity.get("CRITICAL") ?? 0, color: SeverityColors.critical},
		{id: "Low", label: "Low", value: weaknessesBySeverity.get("LOW") ?? 0, color: SeverityColors.low},
		{id: "Info", label: "Info", value: weaknessesBySeverity.get("INFO") ?? 0, color: SeverityColors.info},
		{id: "High", label: "High", value: weaknessesBySeverity.get("HIGH") ?? 0, color: SeverityColors.high},
		{id: "Medium", label: "Medium", value: weaknessesBySeverity.get("MEDIUM") ?? 0, color: SeverityColors.medium},
	]

	console.log(data)

	return (
		<Card>
			<CardHeader title={<Typography variant="h5">Weaknesses by severity</Typography>} action={
					<IconButton aria-label="settings">
						<MoreVert />
					</IconButton>
				}/>
			<Divider />
			<CardContent sx={{width:'100%', maxHeight: 400, aspectRatio: 1 / 1}}>
				<ResponsivePie
					data={data}
					margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
					innerRadius={0.5}
					padAngle={0.6}
					cornerRadius={2}
					activeOuterRadiusOffset={8}
					arcLinkLabelsSkipAngle={10}
					arcLinkLabelsTextColor="#333333"
					arcLinkLabelsThickness={2}
					arcLabelsSkipAngle={10}
					colors={(data) => data.data.color}
					sortByValue
					legends={[
						{
							anchor: 'right',
							direction: 'column',
							translateX: 50,
							translateY: 140,
							itemWidth: 50,
							itemHeight: 18,
							symbolShape: 'circle'
						}
					]}/>
			</CardContent>
		</Card>)
}

class TopVulnerabilityFindingEntryProps {
	position: number;
	vulnerability_id: string;

	constructor(position: number, vulnerability_id: string) {
		this.position = position;
		this.vulnerability_id = vulnerability_id;
	}
}

const TopVulnerabilityFindingEntry = (props: TopVulnerabilityFindingEntryProps) => {
	return (<>
		<ListItem>
			<Typography variant="body1" width={25} justifySelf={'flex-start'}>{props.position+1}</Typography>
			<Divider orientation='vertical' sx={{height: 'auto', alignSelf: "stretch", ml: "auto", mr:"1em"}} />
			<Box width='100%'>
				<VulnerabilityInstanceSimple id={props.vulnerability_id} data={undefined} />
			</Box>
		</ListItem>
		<Divider sx={{alignSelf:"stretch"}} />
	</>)
}

class TopVulnerabilityFindingsProps {
	ids: string[]

	constructor(ids: string[]) {
		this.ids = ids;
	}
}

const TopVulnerabilityFindings = (props: TopVulnerabilityFindingsProps) => {
	return (<>
		<Card>
			<CardHeader title={<Typography variant="h5">Top suspected vulnerabilities</Typography>} action={
					<IconButton aria-label="settings">
						<MoreVert />
					</IconButton>
				}/>
			<Divider />
			<CardContent>
				<List>
				{
					props.ids.map((value, index) => {
						return (<>
							<TopVulnerabilityFindingEntry position={index} vulnerability_id={value} />
						</>)
					})
				}
				</List>
			</CardContent>
		</Card>
	</>)
}

class VulnerabilitiesBySeverityProps {
	vulnerabilitiesBySeverityStr: string

	constructor(vulnerabilitiesBySeverityStr: string) {
		this.vulnerabilitiesBySeverityStr = vulnerabilitiesBySeverityStr;
	}
}

const VulnerabilitiesBySeverity = (props: VulnerabilitiesBySeverityProps) => {
	const vulnerabilitiesBySeverity = new Map(props.vulnerabilitiesBySeverityStr.split(",").map((x) => x.split(":")).map((x) => [x[0], x[1]]))

	const data = [
		{id: "Critical", label: "Critical", value: vulnerabilitiesBySeverity.get("CRITICAL") ?? 0, color: SeverityColors.critical},
		{id: "Low", label: "Low", value: vulnerabilitiesBySeverity.get("LOW") ?? 0, color: SeverityColors.low},
		{id: "Info", label: "Info", value: vulnerabilitiesBySeverity.get("INFO") ?? 0, color: SeverityColors.info},
		{id: "High", label: "High", value: vulnerabilitiesBySeverity.get("HIGH") ?? 0, color: SeverityColors.high},
		{id: "Medium", label: "Medium", value: vulnerabilitiesBySeverity.get("MEDIUM") ?? 0, color: SeverityColors.medium},
	]

	return (<Card>
			<CardHeader title={<Typography variant="h5">Vulnerabilities by severity</Typography>}
				action={
					<IconButton aria-label="settings">
						<MoreVert />
					</IconButton>
				}
			/>
			<Divider />
			<CardContent sx={{width:'100%', maxHeight: 400, aspectRatio: 1 / 1}}>
				<ResponsivePie
					data={data}
					margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
					innerRadius={0.5}
					padAngle={0.6}
					cornerRadius={2}
					activeOuterRadiusOffset={8}
					arcLinkLabelsSkipAngle={10}
					arcLinkLabelsTextColor="#333333"
					arcLinkLabelsThickness={2}
					arcLabelsSkipAngle={10}
					colors={(data) => data.data.color}
					sortByValue
					legends={[
						{
							anchor: 'right',
							direction: 'column',
							translateX: 50,
							translateY: 140,
							itemWidth: 50,
							itemHeight: 18,
							symbolShape: 'circle'
						}
					]}/>
			</CardContent>
		</Card>)
}
